<!--
  Entrar a una aplicación privada.

  Lo que se compara no es el correo a secas: es el correo dentro de esta
  aplicación. La misma dirección puede tener cuenta en otra, con otra clave, y
  esa no abre esta. Ver `loginFor` en `shared/people.ts`.

  La pantalla ya es de la aplicación, no del panel: lleva su paleta y el modo
  claro u oscuro que quien mira eligió para ella (`pageMode`), el mismo que
  verá en cuanto entre.
-->
<script lang="ts">
  import { loginFor } from "@shared/people";
  import type { AppBundle } from "@shared/types";

  import { paletteAttrs } from "../lib/appTheme";
  import { pageMode } from "../lib/pageMode.svelte";
  import { pbApp } from "../lib/pb";
  import AppIcon from "./app/AppIcon.svelte";
  import Icon from "./Icon.svelte";
  import { Button, ErrorNote, Field, Input } from "./ui";
  import ModeToggle from "./ui/ModeToggle.svelte";

  let {
    bundle,
    onSignedIn,
    onCancel,
  }: {
    bundle: AppBundle;
    onSignedIn: () => void | Promise<void>;
    /** En una aplicación pública se entra por gusto: se puede volver sin sesión. */
    onCancel?: () => void;
  } = $props();

  const mode = pageMode(() => bundle.app.id);

  let email = $state("");
  let password = $state("");
  let showPassword = $state(false);
  let busy = $state(false);
  let error = $state("");

  /** Inicia sesión como persona invitada de la aplicación. */
  async function submit(e: SubmitEvent) {
    e.preventDefault();
    // Intro también puede reenviar el formulario con el botón desactivado.
    if (busy) return;
    busy = true;
    error = "";
    try {
      await pbApp
        .collection("members")
        .authWithPassword(loginFor(bundle.app.id, email.trim()), password);
      await onSignedIn();
    } catch {
      error = "Correo o contraseña incorrectos.";
    } finally {
      busy = false;
    }
  }
</script>

<div
  class="login-member flex items-center justify-center"
  data-theme={mode.mode}
  {...paletteAttrs(bundle.app.theme)}
>
  <div class="login-member-mode">
    <ModeToggle
      dark={mode.mode === "dark"}
      onToggle={(next) => mode.set(next)}
      tipLight="Fondo claro"
      tipDark="Fondo oscuro"
      class="btn-login-mode"
    />
  </div>

  <div class="login-member-card">
    <div class="login-member-head">
      <AppIcon app={bundle.app} size={30} class="login-member-appicon" />
      <h1 class="login-member-title">{bundle.app.name}</h1>
      <p class="login-member-hint">Inicia sesión con la cuenta de esta aplicación.</p>
    </div>

    <form
      id="published-form-login"
      onsubmit={submit}
      class="published-form-login login-member-form card card-solid"
    >
      <Field label="Correo">
        <span class="login-member-control">
          <Icon name="mail-01" size={17} class="login-member-lead" />
          <Input
            type="email"
            autocomplete="username"
            placeholder="nombre@ejemplo.com"
            bind:value={email}
            required
          />
        </span>
      </Field>
      <Field label="Contraseña">
        <span class="login-member-control">
          <Icon name="lock-password" size={17} class="login-member-lead" />
          <Input
            type={showPassword ? "text" : "password"}
            autocomplete="current-password"
            bind:value={password}
            required
          />
          <button
            type="button"
            class="btn-toggle-password btn-icon sm btn-ghost login-member-reveal"
            aria-label={showPassword ? "Ocultar la contraseña" : "Mostrar la contraseña"}
            aria-pressed={showPassword}
            data-tip={showPassword ? "Ocultar" : "Mostrar"}
            onclick={() => (showPassword = !showPassword)}
          >
            <Icon name={showPassword ? "view-off" : "view"} size={17} />
          </button>
        </span>
      </Field>
      <ErrorNote message={error} />
      <div class="login-member-actions flex justify-end gap-2">
        {#if onCancel}
          <Button buttonClass="btn-cancel-sign-in" onclick={onCancel}>Volver</Button>
        {/if}
        <Button
          type="submit"
          variant="secondary"
          loading={busy}
          buttonClass="btn-sign-in"
          class="login-member-submit"
        >
          Iniciar sesión
        </Button>
      </div>
    </form>

    <p class="login-member-foot">
      ¿Aún no tienes cuenta? Pide una invitación a quien administra esta aplicación.
    </p>
  </div>
</div>

<style>
  .login-member {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    min-height: 100%;
    padding: 3rem 1rem;
    /* Lleva `data-theme` propio: el fondo lo pone quien lo dibuja. Un halo
       del acento de la aplicación por arriba, para que la entrada ya se
       sienta suya y no una caja gris sobre gris. */
    background:
      radial-gradient(
        60rem 32rem at 50% -8rem,
        color-mix(in oklab, var(--accent) 16%, transparent),
        transparent 70%
      ),
      var(--bg-level1);
    color: var(--text-primary);

    /* Una trama de puntos muy tenue que se desvanece hacia abajo. */
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      z-index: -1;
      background-image: radial-gradient(
        color-mix(in oklab, var(--text-primary) 9%, transparent) 1px,
        transparent 1px
      );
      background-size: 1.5rem 1.5rem;
      mask-image: radial-gradient(40rem 26rem at 50% 0, #000 30%, transparent 80%);
      pointer-events: none;
    }

    & .login-member-mode {
      position: absolute;
      top: 1rem;
      right: 1rem;
    }

    & .login-member-card {
      width: 100%;
      max-width: 23rem;
      animation: login-member-in 0.4s ease-out both;
    }

    & .login-member-head {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: var(--sp-24);
      text-align: center;

      & :global(.login-member-appicon) {
        width: 3.5rem;
        height: 3.5rem;
        margin-bottom: var(--sp-16);
        border-radius: var(--radius-lg);
        box-shadow:
          0 0 0 0.375rem color-mix(in oklab, var(--accent) 8%, transparent),
          var(--shadow-md);
      }

      & .login-member-title {
        font-size: var(--text-2xl);
        line-height: 1.2;
        font-weight: 650;
        letter-spacing: -0.01em;
        color: var(--text-primary);
        text-wrap: balance;
      }

      & .login-member-hint {
        margin-top: var(--sp-6);
        font-size: var(--text-sm);
        line-height: var(--text-sm--line-height);
        color: var(--text-secondary);
      }
    }

    /* La caja es `.card` del catálogo; `card-solid` porque en oscuro la del
       catálogo es translucida y esta se posa sola sobre el lienzo. Aquí solo
       el reparto de dentro: la card no trae acolchado propio, lo ponen su
       cabecera y su cuerpo, y este formulario no tiene ninguno de los dos. */
    & .login-member-form {
      display: grid;
      gap: var(--sp-16);
      padding: var(--sp-24);
      box-shadow: var(--shadow-lg);

      & .login-member-actions {
        margin-top: var(--sp-4);
      }
    }

    /* El campo con su icono delante y, en la contraseña, el ojo detrás. El
       control sigue siendo el del catálogo; solo se le hace sitio. */
    & .login-member-control {
      position: relative;
      display: block;

      & :global(input) {
        height: 2.625rem;
        padding-left: 2.375rem;
      }

      &:has(.login-member-reveal) :global(input) {
        padding-right: 2.75rem;
      }

      & :global(.login-member-lead) {
        position: absolute;
        top: 50%;
        left: 0.75rem;
        translate: 0 -50%;
        color: var(--text-muted);
        pointer-events: none;
        transition: color 0.15s;
      }

      &:focus-within :global(.login-member-lead) {
        color: var(--accent);
      }

      & .login-member-reveal {
        position: absolute;
        top: 50%;
        right: 0.3125rem;
        translate: 0 -50%;
        color: var(--text-muted);

        &:hover {
          color: var(--text-primary);
        }
      }
    }

    & .login-member-foot {
      margin-top: var(--sp-20);
      padding: 0 var(--sp-8);
      font-size: var(--text-xs);
      line-height: var(--text-xs--line-height);
      color: var(--text-muted);
      text-align: center;
      text-wrap: balance;
    }
  }

  @keyframes login-member-in {
    from {
      opacity: 0;
      translate: 0 0.5rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .login-member .login-member-card {
      animation: none;
    }
  }
</style>
